import React, { useEffect, useState } from "react";
import { FaPlus } from "react-icons/fa";
import PermissionsDelete from "./PermissionsDelete";
import PermissionsEdit from "./PermissionsEdit";
import PermissionsGroupService from "../../../services/v2/management/management-service";
import PermissionsAdd from "./PermissionsAdd";
import PermissionsUpdate from "./PermissionsUpdate";
import PermissionsKo from "../../shared/PermissionsKo";

const PermissionsTable = () => {
  const [permissionsGroups, setPermissionsGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isUnauthorized, setIsUnauthorized] = useState(false);

  const fetchPermissionsGroups = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await PermissionsGroupService.list({});
      setPermissionsGroups(response.data.data || []);
    } catch (error) {
      if (error.response && error.response.status === 403) {
        setIsUnauthorized(true);
      }
      setError("Failed to load permissions groups.");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPermissionsGroups();
  }, []);

  const [showAddModal, setShowAddModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [showUpdateModal, setShowUpdateModal] = useState(false);

  const handleDeleteClick = (group) => {
    setSelectedGroup(group);
    setShowDeleteModal(true);
  };

  const handleUpdateClick = (group) => {
    setSelectedGroup(group);
    setShowUpdateModal(true);
  };

  const closeDeleteModal = () => {
    setSelectedGroup(null);
    setShowDeleteModal(false);
  };
  if (isUnauthorized) {
    return <PermissionsKo />;
  }

  const downloadPermissionsCSV = () => {
      const headers = ["Name", "Roles", "Queues", "Users"];

      const rows = permissionsGroups.map((group) => [
        group.name || "",
        Array.isArray(group.roles) ? group.roles.join(" | ") : "",
        group.queues === "all"
          ? "All Queues"
          : Array.isArray(group.queues)
          ? group.queues
              .map((queue) => (queue.startsWith("20") ? queue.slice(5) : queue))
              .join(" | ")
          : "",
        group.users === "all"
          ? "All Users"
          : Array.isArray(group.users)
          ? group.users.map((user) => user.name || user.email || "").join(" | ")
          : "",
      ]);

      const csvContent = [
        headers.join(","),
        ...rows.map((row) =>
          row
            .map((value) => `"${String(value).replace(/"/g, '""')}"`)
            .join(",")
        ),
      ].join("\n");

      const blob = new Blob(["\uFEFF" + csvContent], {
        type: "text/csv;charset=utf-8;",
      });

      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");

      link.href = url;
      link.download = "permission_groups.csv";
      link.click();

      URL.revokeObjectURL(url);
  };

  return (
    <>
      <div className="flex justify-between items-center mt-4">
        <div className="flex items-center gap-2">
          <button
            className="flex items-center gap-2 px-2 py-1 bg-[#2B6CB0] text-white rounded-lg text-sm"
            onClick={() => setShowAddModal(true)}
          >
            <FaPlus />
            Add Group
          </button>

          <button
            className="flex items-center gap-2 px-2 py-1 bg-gray-600 text-white rounded-lg text-sm disabled:opacity-50"
            onClick={downloadPermissionsCSV}
            disabled={loading || permissionsGroups.length === 0}
          >
            Download CSV
          </button>
        </div>

        <PermissionsAdd
          isOpen={showAddModal}
          onClose={() => setShowAddModal(false)}
          onSuccess={fetchPermissionsGroups}
        />

        <PermissionsUpdate
          isOpen={showUpdateModal}
          onClose={() => setShowUpdateModal(false)}
          onSuccess={fetchPermissionsGroups}
          group={selectedGroup}
        />

        {showDeleteModal && (
          <PermissionsDelete
            group={selectedGroup}
            close={closeDeleteModal}
            refresh={fetchPermissionsGroups}
          />
        )}
      </div>
      <div className="max-h-full overflow-auto bg-white dark:bg-gray-800 rounded-md border border-gray-200 dark:border-gray-700 shadow-md">
        <table className="w-full">
          <thead
            className="sticky top-0 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 text-xs uppercase tracking-wide rounded-t-lg"
            style={{ height: "3vh" }}
          >
            <tr>
              {["Name", "Roles", "Queues", "Users", "Actions"].map((header) => (
                <th
                  key={header}
                  className="px-2 py-2 border-b border-gray-300 text-left font-semibold"
                >
                  {header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="5" className="text-center py-10">
                  <div className="flex flex-col items-center text-gray-500 dark:text-gray-400">
                    <svg
                      className="animate-spin h-8 w-8 text-gray-500 dark:text-gray-400"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8v8H4z"
                      ></path>
                    </svg>
                    <p className="mt-3 text-lg">Loading data, please wait...</p>
                  </div>
                </td>
              </tr>
            ) : error ? (
              <tr>
                <td colSpan="5" className="text-center py-10">
                  <div className="flex flex-col items-center text-red-600">
                    <svg
                      className="h-10 w-10"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M12 8v4m0 4h.01M12 2.25C6.443 2.25 2.25 6.443 2.25 12c0 5.558 4.193 9.75 9.75 9.75s9.75-4.192 9.75-9.75C21.75 6.443 17.557 2.25 12 2.25z"
                      />
                    </svg>
                    <p className="mt-3 text-lg font-semibold">
                      Something went wrong!
                    </p>
                    <p className="text-sm">
                      Please reload the page to try again.
                    </p>
                  </div>
                </td>
              </tr>
            ) : permissionsGroups.length === 0 ? (
              <tr>
                <td colSpan="5" className="text-center py-10">
                  <div className="flex flex-col items-center text-gray-500 dark:text-gray-400">
                    <p className="mt-3 text-lg">No data to display.</p>
                    <p className="text-sm">
                      Try adjusting your filters or check back later.
                    </p>
                  </div>
                </td>
              </tr>
            ) : (
              permissionsGroups.map((group, index) => (
                <tr
                  key={group._id}
                  className={`${
                    index % 2 === 0
                      ? "bg-white dark:bg-gray-800"
                      : "bg-gray-50 dark:bg-primary-dark"
                  } hover:bg-gray-100 dark:hover:bg-gray-700 transition-all duration-200`}
                >
                  <td className="px-2 py-2 text-gray-700 dark:text-gray-300 text-sm">
                    {group.name}
                  </td>
                  <td className="px-2 py-2 text-gray-700 dark:text-gray-300 text-sm">
                    <div className="flex gap-1 flex-wrap">
                      {group.roles.map((role, index) => (
                        <span
                          key={index}
                          className="bg-gray-500 dark:bg-gray-600 text-white rounded-full px-2 py-1 text-xs"
                        >
                          {role}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="px-2 py-2 text-gray-700 dark:text-gray-300 text-sm">
                    <div className="flex gap-1 flex-wrap">
                      {group.queues === "all" ? (
                        <span className="bg-purple-500 text-white rounded-full px-2 py-1 text-xs">
                          All Queues
                        </span>
                      ) : (
                        <>
                          {group.queues.slice(0, 5).map((queue, index) => (
                            <span
                              key={index}
                              className="bg-gray-500 dark:bg-gray-600 text-white rounded-full px-2 py-1 text-xs"
                            >
                              {queue.startsWith("20") ? queue.slice(5) : queue}
                            </span>
                          ))}
                          {group.queues.length > 5 && (
                            <span className="bg-gray-500 dark:bg-gray-600 text-white rounded-full px-2 py-1 text-xs">
                              +{group.queues.length - 5}
                            </span>
                          )}
                        </>
                      )}
                    </div>
                  </td>
                  <td className="px-2 py-2 text-gray-700 dark:text-gray-300 text-sm">
                    <div className="flex gap-1 flex-wrap">
                      {group.users === "all" ? (
                        <span className="bg-purple-500 text-white rounded-full px-2 py-1 text-xs">
                          All Users
                        </span>
                      ) : (
                        <>
                          {group.users.slice(0, 5).map((user, index) => (
                            <span
                              key={index}
                              className="bg-gray-500 dark:bg-gray-600 text-white rounded-full px-2 py-1 text-xs"
                            >
                              {user.name}
                            </span>
                          ))}
                          {group.users.length > 5 && (
                            <span className="bg-gray-500 dark:bg-gray-600 text-white rounded-full px-2 py-1 text-xs">
                              +{group.users.length - 5}
                            </span>
                          )}
                        </>
                      )}
                    </div>
                  </td>

                  <td className="px-2 py-2 text-gray-700 dark:text-gray-300 text-sm">
                    {group.isDefault ? (
                      ""
                    ) : (
                      <div className="flex gap-2">
                        <button
                          className="w-6 h-6 flex items-center justify-center bg-gray-200 dark:bg-gray-600 rounded-full"
                          title="Edit"
                          onClick={() => handleUpdateClick(group)}
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            strokeWidth={1.5}
                            stroke="currentColor"
                            className="h-5 w-5"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0 1 15.75 21H5.25A2.25 2.25 0 0 1 3 18.75V8.25A2.25 2.25 0 0 1 5.25 6H10"
                            />
                          </svg>
                        </button>
                        <button
                          className="w-6 h-6 flex items-center justify-center bg-gray-200 dark:bg-gray-600  rounded-full"
                          title="Delete"
                          onClick={() => handleDeleteClick(group)}
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            strokeWidth={1.5}
                            stroke="currentColor"
                            className="h-5 w-5"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0"
                            />
                          </svg>
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </>
  );
};

export default PermissionsTable;
